/**
 * Activities Business Logic Extensions Client - Pure Composition Pattern
 * 
 * Uses ActivitiesClient for basic CRUD operations
 * Adds business logic extensions for status updates and other business operations
 * 
 * These methods call the business logic endpoints in:
 * server/routes/non_generated_single_table_business_logic_routes/activities-business-logic.ts
 */
import { matchesAllTerms } from '../../../config/search/search-utils.js';
import { ActivitiesClient } from '../generated_clients/generated-activities-api-client.js';
import type {
  Activities,
  CreateActivities,
  UpdateActivities,
  ActivitiesQueryOptions
} from '../generated_schemas/generated-activities-schema.js';
import { httpClient } from '../http-client.js';

/**
 * Request type for updating activity status
 */
interface UpdateActivityStatusRequest {
  status_id: string;
}

/**
 * Response type for activity status updates
 * Matches the server response schema from activities business logic routes
 */
interface ActivityStatusUpdateResponse {
  data: Activities;
  success: boolean;
}

/**
 * Pure composition - no inheritance conflicts
 */
export class ActivitiesExtensionsClient {
  constructor(private activitiesClient: ActivitiesClient) {}

  // Delegate all basic operations to ActivitiesClient
  async getAll(params?: ActivitiesQueryOptions): Promise<Activities[]> {
    return this.activitiesClient.getAll(params);
  }

  async getById(id: string): Promise<Activities> {
    return this.activitiesClient.getById(id);
  }

  async create(data: CreateActivities): Promise<Activities> {
    return this.activitiesClient.create(data);
  }

  async update(id: string, data: Partial<UpdateActivities>): Promise<Activities> {
    console.log('[ActivitiesExtensions] Updating activity:', id, 'with data:', data);
    // The generated client expects UpdateActivities (all fields required), not Partial<UpdateActivities>
    // Cast is safe here because the API accepts partial updates
    const result = await this.activitiesClient.update(id, data as UpdateActivities);
    console.log('[ActivitiesExtensions] Update result:', result);
    return result;
  }

  async delete(id: string): Promise<void> {
    return this.activitiesClient.delete(id);
  }

  async count(filter?: Record<string, any>): Promise<number> {
    return this.activitiesClient.count(filter);
  }

  async exists(id: string): Promise<boolean> {
    return this.activitiesClient.exists(id);
  }

  // Business logic extension methods

  /**
   * Update only the status of an activity (convenience method)
   * Uses the simple status endpoint to avoid trigger issues
   * 
   * @param activityId - The activity ID
   * @param statusId - The new status ID
   * @returns Promise with updated activity data
   */
  async updateActivityStatus(activityId: string, statusId: string): Promise<Activities> {
    if (!activityId?.trim()) {
      throw new Error('Activity ID is required');
    }
    
    if (!statusId?.trim()) {
      throw new Error('Status ID is required');
    }

    this.log(`Updating activity ${activityId} status to ${statusId}`);

    try {
      const response = await fetch(`/api/v1/business/activities/${encodeURIComponent(activityId)}/simple-status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status_id: statusId })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      
      if (!data.success) {
        throw new Error('Failed to update activity status');
      }

      this.log(`Successfully updated activity ${activityId} status`);
      return data.data;
    } catch (error: any) {
      // Log error for debugging
      console.error('[ActivitiesExtensions] Error updating activity status:', error);
      
      // Handle specific error cases
      if (error.response?.status === 400) {
        throw new Error('Invalid status ID or status change not allowed');
      } else if (error.response?.status === 403) {
        throw new Error('Insufficient permissions to update activity status');
      } else if (error.response?.status === 404) {
        throw new Error('Activity not found');
      } else if (error.response?.status === 422) {
        throw new Error('Cannot change from terminal status');
      }
      
      // Generic error
      throw new Error(`Failed to update activity status: ${error.message}`);
    }
  }

  /**
   * Filter activities with business logic - calls the correct business endpoint
   * 🔍 Filterable fields: [activity_type_id, entity_type_id, trig_area_lot_id, stage_id, status_id]
   */
  async filter(
    filters: Record<string, any>,
    options?: { limit?: number; offset?: number }
  ): Promise<{ data: Activities[]; success: boolean }> {
    const params = new URLSearchParams();
    
    // Add filters to query params
    Object.entries(filters).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        params.append(`${key}__in`, value.join(','));
      } else if (value === null) {
        params.append(`${key}__null`, 'true');
      } else {
        params.append(key, String(value));
      }
    });
    
    // Add pagination options
    if (options?.limit) {params.append('limit', options.limit.toString());}
    if (options?.offset) {params.append('offset', options.offset.toString());}
    
    const response = await httpClient.get<{
      data: Activities[];
      success: boolean;
    }>(`/activities/business/filter?${params.toString()}`);
    
    return response;
  }

  /**
   * Get all activities with client-side pagination, search, and sorting
   * This method fetches all data and handles pagination client-side like the working workflow
   */
  async getAllWithPagination(options?: {
    searchTerm?: string;
    filters?: Record<string, any>;
    sortBy?: string;
    sortDirection?: 'asc' | 'desc';
    limit?: number;
  }): Promise<{
    data: Activities[];
    totalCount: number;
    success: boolean;
  }> {
    try {
      // Build filter object
      const filter: Record<string, any> = { ...options?.filters };
      
      // Get all activities with a high limit for client-side pagination
      const response = await this.filter(filter, { limit: 1000, offset: 0 });
      
      if (!response.success) {
        return { data: [], totalCount: 0, success: false };
      }
      
      let activities = response.data;
      
      // Apply client-side search if provided
      if (options?.searchTerm) {
        const searchTerm = options.searchTerm;
        activities = activities.filter((activity: Activities) =>
          matchesAllTerms(searchTerm, activity, [
            'name', 'description', 'trig_activity_type_name', 'trig_activity_status_name',
          ])
        );
      }
      
      // Apply client-side sorting if provided
      if (options?.sortBy) {
        activities.sort((a: any, b: any) => {
          const aVal = a[options.sortBy!] || '';
          const bVal = b[options.sortBy!] || '';
          
          if (options.sortDirection === 'desc') {
            return bVal.localeCompare(aVal);
          }
          return aVal.localeCompare(bVal);
        });
      }
      
      return {
        data: activities,
        totalCount: activities.length,
        success: true
      };
    } catch (error) {
      this.log('Error in getAllWithPagination:', error);
      return { data: [], totalCount: 0, success: false };
    }
  }

  /**
   * Get activities by lot ID
   */
  async getActivitiesByLot(lotId: string, params?: ActivitiesQueryOptions): Promise<Activities[]> {
    if (!lotId?.trim()) {
      throw new Error('Lot ID is required');
    }

    const filterParams = { ...params, filters: { ...params?.filters, trig_area_lot_id: lotId } };
    return this.getAll(filterParams);
  }

  /**
   * Get activities by stage ID
   */
  async getActivitiesByStage(stageId: string, params?: ActivitiesQueryOptions): Promise<Activities[]> {
    if (!stageId?.trim()) {
      throw new Error('Stage ID is required');
    }

    const filterParams = { ...params, filters: { ...params?.filters, stage_id: stageId } };
    return this.getAll(filterParams);
  }

  /**
   * Get activities by status ID
   */
  async getActivitiesByStatus(statusId: string, params?: ActivitiesQueryOptions): Promise<Activities[]> {
    if (!statusId?.trim()) {
      throw new Error('Status ID is required');
    }

    const filterParams = { ...params, filters: { ...params?.filters, status_id: statusId } };
    return this.getAll(filterParams);
  }

  // Logging utility
  private log(message: string, data?: any): void {
    console.log(`[ActivitiesExtensions] ${message}`, data || '');
  }
}

// Factory function
export const createActivitiesExtensions = (activitiesClient: ActivitiesClient): ActivitiesExtensionsClient =>
  new ActivitiesExtensionsClient(activitiesClient);

// Export singleton
const activitiesClient = new ActivitiesClient();
export const activitiesExtensions = createActivitiesExtensions(activitiesClient);

// Export types for use in other parts of the application
export type {
  UpdateActivityStatusRequest,
  ActivityStatusUpdateResponse
};